import { Badge, Box, Button, ButtonGroup, Flex, Heading, Input, SimpleGrid, Spinner, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useToast } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { FaBook, FaFileAlt, FaFileImport, FaIdCard, FaSearch } from "react-icons/fa";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import { CommercialClient } from "../../models/CommercialClient";
import { COMPANY_TYPE_OPTIONS, companyTypeLabel } from "../../models/Company";
import { getIntranetClients } from "../../services/intranetService";
import { crm } from "../../theme";
import ClientFichaDrawer from "./ClientFichaDrawer";
import NotionImportModal from "./NotionImportModal";
import ProceduresPanel from "./ProceduresPanel";

// Procedimentos gerais por unidade (1-4); "Todas" (5) não tem página própria
const UNITS = COMPANY_TYPE_OPTIONS.filter((option) => option.value >= 1 && option.value <= 4);

const tabStyle = { borderRadius: "8px", color: crm.textMuted, _selected: { bg: crm.surface, color: crm.brand, boxShadow: crm.shadowSm } };

const Intranet = () => {
  const [clients, setClients] = useState<CommercialClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [unitFilter, setUnitFilter] = useState<number | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [unit, setUnit] = useState(UNITS[0]?.value ?? 1);
  const [importOpen, setImportOpen] = useState(false);
  const [generalDirty, setGeneralDirty] = useState(false);
  const auth = useAuthUser<any>();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const load = async () => {
    try {
      setClients(await getIntranetClients());
    } catch {
      toast({ title: "Não foi possível carregar as fichas.", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ?client=<id> abre a ficha direto (link compartilhável)
  const linked = new URLSearchParams(location.search).get("client");
  useEffect(() => { if (linked) setOpenId(linked); }, [linked]);
  const closeFicha = () => {
    setOpenId(null);
    if (linked) navigate(location.pathname, { replace: true });
  };

  const filtered = useMemo(() => {
    const value = search.trim().toLowerCase();
    return clients.filter((client) =>
      (unitFilter == null || client.companyType === unitFilter)
      && (!value || [client.name, client.cnpj, client.analyst, client.modality, client.dispatcher]
        .some((field) => field?.toLowerCase().includes(value))));
  }, [clients, search, unitFilter]);

  return (
    <Box minH="100vh" bg={crm.pageBg} color={crm.text}>
      <Layout>
        <Box minH="100%" w="100%" bg={crm.pageBg} overflowY="auto" p={{ base: "16px", md: "24px" }}>
          <Flex align="center" gap="12px" mb="22px" wrap="wrap">
            <Box w="4px" h="32px" bg={crm.brand} borderRadius="full" />
            <Heading fontSize="28px" color={crm.text}>Intranet</Heading>
            {/* Endpoint exige admin (InternalAdmin) */}
            {auth?.admin && <Button ml="auto" size="sm" variant="outline" borderColor={crm.borderStrong} leftIcon={<FaFileImport />} onClick={() => setImportOpen(true)}>Importar do Notion</Button>}
          </Flex>
          {/* keepMounted: trocar de aba não desmonta o painel (rascunho não salvo some sem aviso) */}
          <Tabs variant="unstyled" size="sm" isLazy lazyBehavior="keepMounted">
            <TabList bg={crm.subtle} p="4px" borderRadius="10px" display="inline-flex" gap="2px" border="1px solid" borderColor={crm.border} flexWrap="wrap">
              <Tab {...tabStyle}><FaIdCard style={{ marginRight: 7 }} />Fichas de clientes</Tab>
              <Tab {...tabStyle}><FaBook style={{ marginRight: 7 }} />Procedimentos gerais</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px="0" pt="20px">
                <Flex gap="9px" align="center" wrap="wrap" mb="18px">
                  <ButtonGroup size="sm" isAttached variant="outline" flexWrap="wrap">
                    {[{ value: null as number | null, label: "Todas" }, ...UNITS].map(({ value, label }) => (
                      <Button key={label} aria-pressed={unitFilter === value} onClick={() => setUnitFilter(value)} bg={unitFilter === value ? crm.brand : crm.surface} color={unitFilter === value ? "white" : crm.textMuted} borderColor={crm.borderStrong} _hover={{ bg: unitFilter === value ? crm.brandDark : undefined }}>{label}</Button>
                    ))}
                  </ButtonGroup>
                  <Box position="relative" flex={{ base: "1 1 100%", sm: "0 0 auto" }}>
                    <FaSearch style={{ position: "absolute", left: 10, top: 9, color: crm.textMuted, zIndex: 1 }} />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar cliente, CNPJ, analista..." size="sm" w={{ base: "100%", sm: "260px" }} pl="31px" bg={crm.surface} borderColor={crm.borderStrong} />
                  </Box>
                  <Text fontSize="sm" color={crm.textMuted} ml="auto">{filtered.length} ficha{filtered.length === 1 ? "" : "s"}</Text>
                </Flex>
                {loading ? (
                  <Flex py="70px" justify="center"><Spinner thickness="3px" size="xl" /></Flex>
                ) : filtered.length === 0 ? (
                  <Text p="30px" color={crm.textMuted} textAlign="center">Nenhuma ficha encontrada. Fichas vêm dos clientes cadastrados em Comercial › Clientes Ativos.</Text>
                ) : (
                  <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="14px">
                    {filtered.map((client) => <FichaCard key={client.id} client={client} onOpen={() => setOpenId(client.id!)} />)}
                  </SimpleGrid>
                )}
              </TabPanel>
              <TabPanel px="0" pt="20px">
                <ButtonGroup size="sm" isAttached variant="outline" mb="18px" flexWrap="wrap">
                  {UNITS.map(({ value, label }) => (
                    <Button key={value} aria-pressed={unit === value} isDisabled={generalDirty && unit !== value} title={generalDirty ? "Salve ou cancele o procedimento em edição" : undefined} onClick={() => setUnit(value)} bg={unit === value ? crm.brand : crm.surface} color={unit === value ? "white" : crm.textMuted} borderColor={crm.borderStrong} _hover={{ bg: unit === value ? crm.brandDark : undefined }}>{label}</Button>
                  ))}
                </ButtonGroup>
                <Box maxW="900px" bg={crm.surface} border="1px solid" borderColor={crm.border} borderRadius={crm.radius} p={{ base: "14px", md: "20px" }} boxShadow={crm.shadowSm}>
                  <ProceduresPanel scope={{ unitType: unit }} onDirtyChange={setGeneralDirty} emptyText="Nenhum procedimento geral nesta empresa (ex.: documentos cadastrais, habilitação Radar)." />
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Layout>
      <ClientFichaDrawer clientId={openId} onClose={closeFicha} onSaved={load} />
      <NotionImportModal isOpen={importOpen} onClose={() => setImportOpen(false)} clients={clients} onImported={load} />
    </Box>
  );
};

const FichaCard = ({ client, onOpen }: { client: CommercialClient; onOpen: () => void }) => (
  <Box as="button" type="button" textAlign="left" onClick={onOpen} bg={crm.surface} border="1px solid" borderColor={crm.border} borderRadius={crm.radius} boxShadow={crm.shadowSm} overflow="hidden" transition="border-color .15s, box-shadow .15s" _hover={{ borderColor: crm.brand, boxShadow: crm.shadowMd }} _focusVisible={{ outline: `2px solid ${crm.brand}`, outlineOffset: "2px" }}>
    <Box h="6px" bg={crm.brand} />
    <Box p="14px">
      <Text fontWeight="700" fontSize="md" noOfLines={2} color={crm.text}>{client.name}</Text>
      <Text fontSize="xs" color={crm.textMuted} mt="2px">{companyTypeLabel(client.companyType) || "—"}{client.cnpj ? ` · ${client.cnpj}` : ""}</Text>
      <Flex gap="6px" mt="10px" wrap="wrap" minH="20px">
        {client.modality && <Badge colorScheme="blue" variant="subtle" textTransform="none">{client.modality}</Badge>}
        {client.analyst && <Badge colorScheme="gray" variant="subtle" textTransform="none">{client.analyst}</Badge>}
      </Flex>
      <Flex align="center" gap="6px" mt="10px" fontSize="xs" color={crm.textMuted}>
        <FaFileAlt />
        <Text>{client.proceduresCount ? `${client.proceduresCount} procedimento${client.proceduresCount > 1 ? "s" : ""}` : "Sem procedimentos"}</Text>
      </Flex>
    </Box>
  </Box>
);

export default Intranet;
